<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

import { parseKitDetails } from '@/entrypoints/helpers/cart-helpers/cart-line-kit-details.ts';
import HobbiiCoinImg from '@/images/hobbii-coin.png';
import IconDelete from '@/shared/components/UI/Icons/IconDelete.vue';
import IconDiscountCode from '@/shared/components/UI/Icons/IconDiscountCode.vue';
import NumberSpinner from '@/shared/components/UI/NumberSpinner.vue';
import { useTranslations } from '@/shared/composables/useTranslations';
import { useCartStore } from '@/shared/pinia/modules/cart';
import { getLineItemUpdateKey } from '@/shared/utils/cart';
import {
  formattedPriceWithDecimals as formattedPrice,
  parseCompareAtPrice
} from '@/shared/utils/shopify/money-formatter';

import KitItems from './KitItems.vue';

const props = defineProps<{
  item: ShopifyCartItem;
  lineNumber: number;
}>();

const cartStore = useCartStore();
const { loadingCart } = storeToRefs(cartStore);
const { t } = useTranslations();

const freeRewardDiscountMsg = t('cart.free_reward_discount');
const isFreeReward = computed(() => {
  const discounts =
    props.item.discounts ?? props.item.line_level_discount_allocations;
  if (!discounts?.length) return false;
  const firstTitle =
    discounts[0]?.title ??
    (discounts[0] as { discount_application?: { title?: string } })
      ?.discount_application?.title;
  return firstTitle === freeRewardDiscountMsg;
});

const isGiftCard = computed(
  () => props.item.gift_card || props.item.product?.gift_card
);

const isPattern = computed(() => {
  if (props.item.product?.metafields?.hobbii?.entity_type === 'pattern') {
    return true;
  }
  // Fallback when product.metafields missing: digital, non-gift-card items
  return props.item.requires_shipping === false && !isGiftCard.value;
});

const disabledQuantitySelector = computed(
  () => isPattern.value || isFreeReward.value
);

const imageUrl = computed(() => {
  const url = props.item.image ?? props.item.featured_image?.url;
  if (url) return url;
  return window.Hobbii?.fallbackImageUrl ?? '';
});

const productUrl = computed(() => {
  const url = props.item.url;
  if (url) return url.startsWith('/') ? url : `/${url}`;
  const handle = props.item.handle;
  const variantId = props.item.variant_id ?? props.item.id;
  return handle ? `/products/${handle}?variant=${variantId}` : '#';
});

const productTitle = computed(
  () => props.item.product_title ?? props.item.title ?? ''
);

const removeAriaLabel = computed(() =>
  `${t('cart.remove_item')} ${productTitle.value}`.trim()
);

const variantTitle = computed(() => props.item.variant_title ?? '');

const showVariantTitle = computed(
  () => variantTitle.value && variantTitle.value !== 'Default Title'
);

const lineLevelDiscounts = computed(() => {
  const allocations =
    props.item.discounts ?? props.item.line_level_discount_allocations ?? [];
  return allocations
    .map((a) => {
      return (
        (a as { title?: string }).title ??
        (a as { discount_application?: { title?: string } })
          .discount_application?.title ??
        ''
      );
    })
    .filter(Boolean);
});

const maxOriginalOrCompareAtPrice = computed(() => {
  const original = props.item.original_price ?? 0;
  let compareAt = 0;
  if (props.item.variant?.compare_at_price) {
    compareAt = parseCompareAtPrice(props.item.variant.compare_at_price);
  }
  return Math.max(original, compareAt);
});

const maxOriginalOrCompareAtLinePrice = computed(
  () => maxOriginalOrCompareAtPrice.value * (props.item.quantity || 1)
);

const finalPrice = computed(() => props.item.final_price ?? 0);
const lineHasDiscount = computed(
  () => finalPrice.value !== maxOriginalOrCompareAtPrice.value
);

const priceInPoints = computed(() =>
  cartStore.getPointPriceForItem(props.item)
);

const formattedFinalLinePrice = computed(() =>
  formattedPrice(props.item.final_line_price ?? 0)
);

const formattedCompareAtLinePrice = computed(() =>
  formattedPrice(maxOriginalOrCompareAtLinePrice.value)
);

const lineKey = computed(() => getLineItemUpdateKey(props.item));

const lineError = computed(() =>
  cartStore.errors.find((e) => e.lineKey === lineKey.value)
);

const handleQuantityChange = (newQuantity: number) => {
  cartStore.cartChangeQty(props.lineNumber, newQuantity);
};

const handleRemove = () => {
  cartStore.cartChangeQty(props.lineNumber, 0);
};

const kitItems = computed(() => {
  const raw = props.item.properties?.['_Details'];
  if (!raw) return null;
  const entries = parseKitDetails(raw);
  return entries.length > 0 ? entries : null;
});
</script>

<template>
  <div :data-cart-line-number="lineNumber">
    <div class="py-4">
      <div class="flex w-full items-start gap-4">
        <a
          :href="productUrl"
          tabindex="-1"
          aria-hidden="true"
          class="w-24 shrink-0"
        >
          <img
            v-if="imageUrl"
            class="block aspect-square w-full rounded-md"
            :src="imageUrl"
            height="150"
            width="150"
            loading="lazy"
            alt=""
          />
        </a>

        <div class="flex grow flex-col gap-4">
          <div class="flex">
            <div
              class="flex grow flex-col gap-1 leading-none"
              name="cart-item-details"
            >
              <a
                :href="productUrl"
                :title="productTitle"
                class="text-sm font-bold leading-tight"
              >
                {{ productTitle }}
                <span v-if="lineError" class="js-cart-line-error text-error"
                  >&nbsp;*</span
                >
              </a>

              <span v-if="showVariantTitle" class="text-xs text-black-500">
                {{ variantTitle }}
              </span>

              <div
                v-if="lineLevelDiscounts.length > 0"
                class="flex flex-wrap items-center gap-0.5"
              >
                <span
                  v-for="(title, idx) in lineLevelDiscounts"
                  :key="idx"
                  class="inline-flex items-center gap-0.5 rounded-md bg-gray-100 px-1 py-0.5 text-xs text-gray-500"
                >
                  <IconDiscountCode class="shrink-0 text-xs" />
                  {{ title }}
                </span>
              </div>

              <div class="hidden md:mt-2 md:block">
                <button
                  type="button"
                  :title="t('cart.remove_from_cart')"
                  :aria-label="removeAriaLabel"
                  :disabled="loadingCart"
                  class="flex items-center gap-0.5 text-xs leading-tight text-gray-500 underline transition-colors hover:cursor-pointer hover:text-gray-600 disabled:cursor-wait disabled:opacity-40"
                  @click="handleRemove"
                >
                  <IconDelete size="md" use-current-color aria-hidden="true" />
                  <span class="hidden md:inline">{{
                    t('cart.remove_item')
                  }}</span>
                </button>
              </div>

              <KitItems v-if="kitItems" :items="kitItems" />
            </div>

            <div class="flex items-start gap-4">
              <div class="flex flex-col items-end gap-2 text-sm">
                <template v-if="priceInPoints > 0">
                  <div class="inline-flex items-center gap-1 text-sm">
                    <img
                      :src="HobbiiCoinImg"
                      class="w-4 md:w-4"
                      width="20"
                      height="20"
                      loading="lazy"
                      alt=""
                    />
                    <span>
                      {{ priceInPoints * item.quantity }}
                      {{ t('cart.points') }}
                    </span>
                  </div>
                </template>
                <template v-else>
                  <div
                    v-if="lineHasDiscount"
                    class="flex items-center gap-2 text-sm"
                  >
                    <span class="text-black-500 line-through">
                      {{ formattedCompareAtLinePrice }}
                    </span>
                    <span class="font-bold text-pink">
                      {{ formattedFinalLinePrice }}
                    </span>
                  </div>
                  <div v-else class="text-sm font-bold">
                    {{ formattedFinalLinePrice }}
                  </div>
                </template>

                <div
                  v-if="!disabledQuantitySelector"
                  :data-line-number="lineNumber"
                  class="relative flex items-center self-end max-md:hidden md:flex"
                  :class="{ 'pointer-events-none opacity-40': loadingCart }"
                >
                  <NumberSpinner
                    :key="item.key ?? item.id"
                    :model-value="item.quantity"
                    :product-name="productTitle"
                    :min="0"
                    size="compact"
                    wrapper-class="border-gray"
                    button-class="text-black-600 disabled:text-black-400"
                    input-class="w-8 text-base"
                    class="bg-white"
                    @update:model-value="handleQuantityChange"
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-between md:hidden">
            <div
              v-if="!disabledQuantitySelector"
              :data-line-number="lineNumber"
              class="mr-auto flex items-center"
              :class="{ 'pointer-events-none opacity-40': loadingCart }"
            >
              <NumberSpinner
                :key="item.key ?? item.id"
                :min="0"
                :model-value="item.quantity"
                :product-name="productTitle"
                size="compact"
                wrapper-class="border-gray"
                button-class="text-black-600 disabled:text-black-400"
                input-class="w-8 text-base"
                class="bg-white"
                @update:model-value="handleQuantityChange"
              />
            </div>
            <button
              type="button"
              :title="t('cart.remove_from_cart')"
              :aria-label="removeAriaLabel"
              :disabled="loadingCart"
              class="flex size-6 items-center justify-center text-gray-500 hover:text-gray-600 disabled:cursor-wait disabled:opacity-40 md:hidden"
              @click="handleRemove"
            >
              <IconDelete size="md" use-current-color aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="lineError" class="js-cart-line-error pb-2 text-xs md:right-auto">
      <div
        class="flex rounded bg-error-100 px-2 py-1 text-left text-black/90 md:text-center"
      >
        <span class="pr-1 text-error-800">* </span>
        <span class="js-cart-line-error-msg text-error-900">{{
          lineError.message
        }}</span>
      </div>
    </div>
  </div>
</template>
